import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  DollarSign,
  MapPin,
  Users,
  FileText,
  Clock,
  MessageSquare,
  FolderOpen,
  BarChart3,
  CheckCircle2,
  Plus,
  Trash2,
  AlertTriangle,
  GitBranch,
  GanttChart,
  QrCode,
  Activity,
  Download,
  Send,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import {
  useProject,
  useProjectSummary,
  useProjectCostCodes,
  useUpdateProject,
  useUpdateProjectStatus,
  useProjectTasks,
  useCreateProjectTask,
  useUpdateProjectTask,
  useDeleteProjectTask,
  useProjectAssignments,
  useCreateProjectAssignment,
  useDeleteProjectAssignment,
  useProjectTimeEntries,
  useCreateProjectTimeEntry,
  useUpdateTimeEntry,
  useApproveTimeEntry,
  useProjectTimeSummary,
  useInvoices,
  useChangeOrders,
  useCreateChangeOrder,
  useApproveChangeOrder,
  useRejectChangeOrder,
  useCreateChangeOrderLineItem,
  useDeleteChangeOrderLineItem,
  useProjectQRCode,
  useExportAuditBundle,
  useDownloadProofPackPdf,
  useProgressSummary,
  useGenerateProgressInvoice,
  useProjectPhases,
  useCreateProjectPhase,
  useUpdateProjectPhase,
  useDeleteProjectPhase,
} from '@/hooks/use-projects';
import { usePermissions } from '@/hooks/use-permissions';
import { CreatePurchaseOrderButton } from '@/app/pages/purchase-orders/create-purchase-order-button';
import { useAuth } from '@/contexts/auth-context';
import { useOrgCurrency, useOrganizationSettings, useOrgTimeZone } from '@/hooks/use-settings';
import { useShowInvoicing } from '@/hooks/use-show-invoicing';
import { QRCodeModal } from '@/components/safety/qr-code-modal';
import { ModuleDisabledState } from '@/app/components/module-disabled-state';
import { ProjectSafetyTab, ProjectGearTab, ProjectCrewTab } from '@/app/components/cross-module/project-cross-module-tabs';
import { useZones } from '@/hooks/use-zones';
import { useSpots } from '@/hooks/use-spots';
import { formatMoney, OPTIONAL_FRACTION_DIGITS } from '@/lib/currency';
import { formatDateOnly, formatDateTimeInTimeZone, getLocalToday } from '@/lib/date-utils';
import { PreviousDayJSAPrompt } from '@/app/components/safety/previous-day-jsa-prompt';
import { toast } from 'sonner';
import { handleApiError } from '@/lib/api-client';
import { PersonSearchComboBox } from '@/components/shared/person-search-combo-box';
import type { PersonOption } from '@/components/shared/person-search-combo-box';
import { ZonePicker } from '@/components/shared/zone-picker';
import type {
  BillRateUnit,
  Project,
  ProjectPhase,
  ProjectRateCardEntry,
  ProjectStatus,
  ProjectTaskStatus,
  ChangeOrderStatus,
  CreateTimeEntryPayload,
  TimeEntry,
} from '@/lib/types';
import { billRateUnitSuffix } from '@/lib/bill-rate';
import { CostCodeSelector } from '@/app/components/cost-code-selector';
import { GanttTab } from './tabs/gantt-tab';
import { ProjectActivityTab } from './tabs/project-activity-tab';
import { NotesTab } from './tabs/notes-tab';
import { DocumentsTab } from './tabs/documents-tab';
import { humanizeEnum } from '@/lib/labels';
import { JobPayRatesPanel } from '@/app/pages/payroll/rates/job-pay-rates-panel';
import { ApprovalError, useSelfApproval } from '@/app/components/payroll/self-approval';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  bidding: 'bg-blue-100 text-blue-800',
  awarded: 'bg-purple-100 text-purple-800',
  active: 'bg-green-100 text-green-800',
  on_hold: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
};

const taskStatusColors: Record<string, string> = {
  todo: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  blocked: 'bg-red-100 text-red-800',
};

type Tab = 'overview' | 'tasks' | 'team' | 'time' | 'documents' | 'notes' | 'financials' | 'change_orders' | 'progress' | 'gantt' | 'safety' | 'gear' | 'crew' | 'activity';

const ALL_TABS: { key: Tab; label: string; icon: React.ElementType; gate?: 'safety' | 'inventory' | 'crew' | 'projects' | 'payroll' | 'time' }[] = [
  { key: 'overview', label: 'Overview', icon: Briefcase },
  { key: 'tasks', label: 'Tasks', icon: CheckCircle2, gate: 'projects' },
  { key: 'team', label: 'Team', icon: Users },
  { key: 'safety', label: 'Safety', icon: FileText },
  { key: 'gear', label: 'Gear', icon: Calendar },
  { key: 'crew', label: 'Crew', icon: Users, gate: 'crew' },
  // Logging time is a core capability (D1): available with H3Pay or H3Books.
  { key: 'time', label: 'Time', icon: Clock, gate: 'time' },
  { key: 'documents', label: 'Documents', icon: FolderOpen, gate: 'projects' },
  { key: 'notes', label: 'Notes', icon: MessageSquare },
  { key: 'financials', label: 'Financials', icon: BarChart3, gate: 'projects' },
  { key: 'progress', label: 'Progress/SOV', icon: DollarSign, gate: 'projects' },
  { key: 'change_orders', label: 'Change Orders', icon: GitBranch, gate: 'projects' },
  { key: 'activity', label: 'Activity', icon: Activity },
  { key: 'gantt', label: 'Gantt', icon: GanttChart, gate: 'projects' },
];

export function ProjectDetailPage({ backPath = '/projects/list' }: { backPath?: string }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const projectId = parseInt(id ?? '0');
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const [showQR, setShowQR] = useState(false);
  const { data: project, isLoading } = useProject(projectId);
  const { data: qrData, isLoading: qrLoading } = useProjectQRCode(projectId, showQR);
  const updateStatus = useUpdateProjectStatus();
  const updateProject = useUpdateProject();
  const exportAuditBundle = useExportAuditBundle(projectId);
  const downloadProofPack = useDownloadProofPackPdf();
  const { isAdmin } = usePermissions();
  const { data: orgSettings, isLoading: orgSettingsLoading } = useOrganizationSettings();

  const safetyEnabled = orgSettings?.modules.safety ?? false;
  const inventoryEnabled = orgSettings?.modules.inventory ?? false;
  const crewEnabled = orgSettings?.modules.crew ?? false;
  const projectsEnabled = orgSettings?.modules.projects ?? false;
  const payrollEnabled = orgSettings?.modules.payroll ?? false;
  const showInvoicing = useShowInvoicing();
  // The summary endpoint is a financial rollup gated on the projects
  // module — skip the request entirely when H3Books is off.
  const { data: summary } = useProjectSummary(projectId, projectsEnabled);

  const moduleGates: Record<string, boolean> = {
    safety: safetyEnabled,
    inventory: inventoryEnabled,
    crew: crewEnabled,
    projects: projectsEnabled,
    payroll: payrollEnabled,
    time: payrollEnabled || projectsEnabled,
  };

  const tabs = useMemo(
    () => ALL_TABS.filter((t) => !t.gate || moduleGates[t.gate]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [safetyEnabled, inventoryEnabled, crewEnabled, projectsEnabled, payrollEnabled],
  );

  useEffect(() => {
    if (!tabs.some((t) => t.key === activeTab)) {
      setActiveTab('overview');
    }
  }, [tabs, activeTab]);

  const handleExportAuditBundle = useCallback(async () => {
    try {
      const job = await exportAuditBundle.mutateAsync({});
      if (job.download_url) {
        window.open(job.download_url, '_blank', 'noopener');
      } else {
        alert(
          job.status === 'failed'
            ? `Audit bundle failed: ${job.error_message || 'unknown error'}`
            : 'Audit bundle queued. Check back shortly.',
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert(`Failed to export audit bundle: ${message}`);
    }
  }, [exportAuditBundle]);

  if (isLoading || orgSettingsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Project not found</p>
      </div>
    );
  }

  const handleStatusChange = async (status: ProjectStatus) => {
    try {
      if (projectsEnabled) {
        await updateStatus.mutateAsync({ id: projectId, status });
      } else {
        // The transition endpoint requires the projects module; the basic
        // status field is writable via PATCH in any module combination.
        await updateProject.mutateAsync({ id: projectId, data: { status } });
      }
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message || 'Failed to update project status');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PreviousDayJSAPrompt projectId={projectId} enabled={safetyEnabled} />
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Projects
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <Badge className={statusColors[project.status ?? ''] ?? ''}>
              {project.status?.replace('_', ' ') ?? ''}
            </Badge>
          </div>
          <p className="text-gray-500 mt-1">
            {project.project_number}
            {project.client_name ? ` — ${project.client_name}` : ''}
            {project.department_name ? ` · ${project.department_name}` : ''}
          </p>
        </div>
        <div className="flex gap-2">
          {safetyEnabled && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/projects/${projectId}/jsas`}>
                <FileText className="h-4 w-4 mr-1" />
                JSAs
              </Link>
            </Button>
          )}
          {inventoryEnabled && (
            <Button variant="outline" size="sm" asChild data-testid="project-send-gear">
              <Link to={`/deployments/send-gear?project=${projectId}`}>
                <Send className="h-4 w-4 mr-1" />
                Send gear
              </Link>
            </Button>
          )}
          <CreatePurchaseOrderButton
            projectId={projectId}
            projectStatus={project.status}
            purchasingEnabled={projectsEnabled}
          />
          {projectsEnabled && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadProofPack.mutate(projectId)}
              disabled={downloadProofPack.isPending}
            >
              <Download className="h-4 w-4 mr-1" />
              {downloadProofPack.isPending ? 'Generating…' : 'Generate proof pack'}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportAuditBundle}
              disabled={exportAuditBundle.isPending}
              title="Download IRATA/SPRAT audit bundle (ZIP)"
            >
              <Download className="h-4 w-4 mr-1" />
              {exportAuditBundle.isPending ? 'Exporting…' : 'Export audit bundle'}
            </Button>
          )}
          {safetyEnabled && (
            <Button variant="outline" size="icon" onClick={() => setShowQR(true)}>
              <QrCode className="h-4 w-4" />
            </Button>
          )}
          <div className="flex flex-col gap-1" data-testid="project-status-control">
            <Label htmlFor="project-status-select" className="text-xs font-medium text-gray-500">
              Status
            </Label>
            <Select value={project.status ?? ''} onValueChange={(val) => handleStatusChange(val as ProjectStatus)}>
              <SelectTrigger id="project-status-select" aria-label="Project status" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['draft', 'bidding', 'awarded', 'active', 'on_hold', 'completed', 'cancelled'].map(
                  (s) => (
                    <SelectItem key={s} value={s}>
                      {s.replace('_', ' ')}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
            {project.status === 'draft' && (
              <p className="text-xs text-amber-700 max-w-[16rem]" data-testid="project-status-draft-hint">
                Draft projects are activated automatically when you dispatch crew.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab key={project.id} project={project} summary={summary} projectsEnabled={projectsEnabled} payrollEnabled={payrollEnabled} showInvoicing={showInvoicing} />}
      {activeTab === 'tasks' && <TasksTab projectId={projectId} />}
      {activeTab === 'team' && <TeamTab projectId={projectId} project={project} projectsEnabled={projectsEnabled} />}
      {activeTab === 'time' && <TimeTab projectId={projectId} payrollEnabled={payrollEnabled} projectsEnabled={projectsEnabled} />}
      {activeTab === 'documents' && <DocumentsTab projectId={projectId} />}
      {activeTab === 'notes' && <NotesTab projectId={projectId} />}
      {activeTab === 'safety' && <ProjectSafetyTab projectId={projectId} safetyEnabled={safetyEnabled} />}
      {activeTab === 'gear' && <ProjectGearTab projectId={projectId} inventoryEnabled={inventoryEnabled} />}
      {activeTab === 'crew' && <ProjectCrewTab projectId={projectId} crewEnabled={crewEnabled} />}
      {activeTab === 'financials' && <FinancialsTab projectId={projectId} summary={summary} project={project} showInvoicing={showInvoicing} />}
      {activeTab === 'progress' && <ProgressTab projectId={projectId} project={project} />}
      {activeTab === 'change_orders' && <ChangeOrdersTab projectId={projectId} />}
      {activeTab === 'activity' && <ProjectActivityTab projectId={projectId} />}
      {activeTab === 'gantt' && <GanttTab projectId={projectId} />}

      <QRCodeModal
        open={showQR}
        onOpenChange={setShowQR}
        qrDataUrl={qrData?.qr_data_url}
        title={project.name}
        subtitle={project.location}
        isLoading={qrLoading}
      />
    </div>
  );
}

// ─── Overview Tab ───────────────────────────────────────────────────────────

export function OverviewTab({
  project,
  summary,
  projectsEnabled,
  payrollEnabled,
  showInvoicing,
}: {
  project: NonNullable<ReturnType<typeof useProject>['data']>;
  summary: ReturnType<typeof useProjectSummary>['data'];
  projectsEnabled: boolean;
  payrollEnabled: boolean;
  showInvoicing: boolean;
}) {
  const currency = useOrgCurrency();
  const { canManageJobs } = usePermissions();
  const updateProject = useUpdateProject();
  const [startDate, setStartDate] = useState(project.start_date ?? '');
  const [endDate, setEndDate] = useState(project.end_date ?? '');
  const previousProjectStartDate = useRef(project.start_date ?? '');
  const previousProjectEndDate = useRef(project.end_date ?? '');

  useEffect(() => {
    const nextStartDate = project.start_date ?? '';
    const nextEndDate = project.end_date ?? '';
    const previousStartDate = previousProjectStartDate.current;
    const previousEndDate = previousProjectEndDate.current;
    setStartDate((current) =>
      current === previousStartDate ? nextStartDate : current
    );
    setEndDate((current) =>
      current === previousEndDate ? nextEndDate : current
    );
    previousProjectStartDate.current = nextStartDate;
    previousProjectEndDate.current = nextEndDate;
  }, [project.start_date, project.end_date]);

  const handleSaveDates = async () => {
    if (startDate && endDate && endDate < startDate) {
      toast.error('End date must be on or after the start date.');
      return;
    }
    try {
      await updateProject.mutateAsync({
        id: project.id,
        data: {
          start_date: startDate || null,
          end_date: endDate || null,
        },
      });
      toast.success('Project dates updated');
    } catch (err) {
      toast.error(handleApiError(err).message || 'Failed to update project dates');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* Key Metrics */}
        {projectsEnabled && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500">Budget</p>
                <p className="text-xl font-bold">
                  {project.budget ? formatMoney(project.budget, currency, OPTIONAL_FRACTION_DIGITS) : '—'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500">Actual Costs</p>
                <p className="text-xl font-bold">
                  {summary ? formatMoney(summary.total_costs || '0', currency, OPTIONAL_FRACTION_DIGITS) : '—'}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-sm text-gray-500">Hours Logged</p>
                <p className="text-xl font-bold">{summary?.total_hours ?? '—'}</p>
              </CardContent>
            </Card>
            {showInvoicing && (
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-500">Total Invoiced</p>
                  <p className="text-xl font-bold">
                    {summary ? formatMoney(summary.total_invoiced || '0', currency, OPTIONAL_FRACTION_DIGITS) : '—'}
                  </p>
                </CardContent>
              </Card>
            )}
            {project.contract_value && (
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-500">Contract Value</p>
                  <p className="text-xl font-bold">
                    {formatMoney(project.current_contract_value ?? project.contract_value ?? '0', currency, OPTIONAL_FRACTION_DIGITS)}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Description */}
        {project.description && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 whitespace-pre-wrap">{project.description}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {canManageJobs ? (
              <>
                <div className="space-y-1">
                  <Label htmlFor="project-start-date" className="text-xs text-gray-500">
                    Start Date
                  </Label>
                  <Input
                    id="project-start-date"
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="project-end-date" className="text-xs text-gray-500">
                    End Date
                  </Label>
                  <Input
                    id="project-end-date"
                    type="date"
                    min={startDate || undefined}
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSaveDates}
                  disabled={updateProject.isPending}
                >
                  {updateProject.isPending ? 'Saving…' : 'Save Dates'}
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Start:</span>
                  <span>{formatDateOnly(project.start_date, 'Not set')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">End:</span>
                  <span>
                    {projectsEnabled && project.end_date === null ? (
                      <span className="text-blue-600 font-medium">Ongoing</span>
                    ) : (
                      formatDateOnly(project.end_date, 'Not set')
                    )}
                  </span>
                </div>
              </>
            )}
            {project.location && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-400" />
                <span>{project.location}</span>
              </div>
            )}
            {showInvoicing && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Billing:</span>
                <span>{humanizeEnum(project.billing_type)}</span>
              </div>
            )}
            {showInvoicing && project.billing_rate && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">Rate:</span>
                <span>
                  {formatMoney(project.billing_rate, currency, OPTIONAL_FRACTION_DIGITS)}
                  {billRateUnitSuffix(project.billing_rate_unit)}
                </span>
              </div>
            )}
            {project.po_number && (
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">PO:</span>
                <span>{project.po_number}</span>
              </div>
            )}
          </CardContent>
        </Card>
        {showInvoicing && payrollEnabled && <RateCardSection project={project} />}
        <JobPayRatesPanel project={project} />
        {project.location_fk != null && (
          <ProjectZoneSpotSection projectId={project.id} locationId={project.location_fk} zone={project.zone} spot={project.spot} />
        )}
      </div>
    </div>
  );
}

type RateCardRowState = {
  rowId: string;
  cost_code: number | '';
  hourly_rate: string;
  rate_unit: BillRateUnit;
  overtime_rate: string;
  doubletime_rate: string;
};

function makeRateCardRowState(entry?: ProjectRateCardEntry): RateCardRowState {
  return {
    rowId: `rate-card-${entry?.id ?? Math.random().toString(36).slice(2, 10)}`,
    cost_code: entry?.cost_code ?? '',
    hourly_rate: entry?.hourly_rate ?? '',
    rate_unit: entry?.rate_unit ?? 'hourly',
    overtime_rate: entry?.overtime_rate ?? '',
    doubletime_rate: entry?.doubletime_rate ?? '',
  };
}

function RateCardSection({
  project,
}: {
  project: NonNullable<ReturnType<typeof useProject>['data']>;
}) {
  const updateProject = useUpdateProject();
  const { data: costCodes } = useProjectCostCodes(project.id);
  const [minimumCalloutHours, setMinimumCalloutHours] = useState(project.minimum_callout_hours ?? '');
  const [rows, setRows] = useState<RateCardRowState[]>(
    () => (project.rate_card_entries ?? []).map((entry) => makeRateCardRowState(entry)),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMinimumCalloutHours(project.minimum_callout_hours ?? '');
    setRows((project.rate_card_entries ?? []).map((entry) => makeRateCardRowState(entry)));
    setError(null);
  }, [project.id, project.minimum_callout_hours, project.rate_card_entries]);

  const hasIncompleteRow = rows.some(
    (row) => row.cost_code === '' || row.hourly_rate.trim() === '',
  );

  const handleAddRow = () => {
    setRows((current) => [...current, makeRateCardRowState()]);
  };

  const handleRemoveRow = (rowId: string) => {
    setRows((current) => current.filter((row) => row.rowId !== rowId));
  };

  const handleSave = async () => {
    if (hasIncompleteRow) {
      setError('Fill in all rate card rows before saving.');
      return;
    }

    setError(null);
    try {
      await updateProject.mutateAsync({
        id: project.id,
        data: {
          minimum_callout_hours: minimumCalloutHours.trim() === '' ? null : minimumCalloutHours.trim(),
          rate_card_entries: rows.map((row) => ({
            cost_code: row.cost_code as number,
            hourly_rate: row.hourly_rate.trim(),
            rate_unit: row.rate_unit,
            overtime_rate: row.overtime_rate.trim() === '' ? null : row.overtime_rate.trim(),
            doubletime_rate: row.doubletime_rate.trim() === '' ? null : row.doubletime_rate.trim(),
          })),
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save rate card.';
      setError(message);
      alert(message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">Rate Card</CardTitle>
          <Button type="button" variant="outline" size="sm" onClick={handleAddRow}>
            <Plus className="h-4 w-4 mr-1" />
            Add line
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-1">
          <Label htmlFor="minimum_callout_hours" className="text-xs text-gray-500">
            Minimum call-out hours
          </Label>
          <Input
            id="minimum_callout_hours"
            type="number"
            min="0"
            step="0.01"
            value={minimumCalloutHours}
            onChange={(e) => setMinimumCalloutHours(e.target.value)}
            placeholder="e.g. 2.00"
          />
        </div>

        <div className="space-y-3">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-500">No rate card lines yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-3">Cost code</th>
                    <th className="py-2 pr-3">Base rate</th>
                    <th className="py-2 pr-3">Unit</th>
                    <th className="py-2 pr-3">OT rate</th>
                    <th className="py-2 pr-3">DT rate</th>
                    <th className="py-2 pr-3" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const selectedCostCodeIds = new Set(
                      rows
                        .filter((otherRow) => otherRow.rowId !== row.rowId && otherRow.cost_code !== '')
                        .map((otherRow) => otherRow.cost_code as number),
                    );
                    const options = (costCodes ?? []).filter(
                      (cc) => cc.id === row.cost_code || !selectedCostCodeIds.has(cc.id),
                    );

                    return (
                      <tr key={row.rowId} className="border-b last:border-b-0 align-top">
                        <td className="py-2 pr-3">
                          <Select
                            value={row.cost_code === '' ? '' : row.cost_code.toString()}
                            onValueChange={(value) =>
                              setRows((current) =>
                                current.map((currentRow) =>
                                  currentRow.rowId === row.rowId
                                    ? {
                                        ...currentRow,
                                        cost_code: parseInt(value, 10),
                                      }
                                    : currentRow,
                                ),
                              )
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select cost code" />
                            </SelectTrigger>
                            <SelectContent>
                              {options.map((cc) => (
                                <SelectItem key={cc.id} value={cc.id.toString()}>
                                  {cc.code} — {cc.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 pr-3">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.hourly_rate}
                            onChange={(e) =>
                              setRows((current) =>
                                current.map((currentRow) =>
                                  currentRow.rowId === row.rowId
                                    ? { ...currentRow, hourly_rate: e.target.value }
                                    : currentRow,
                                ),
                              )
                            }
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <Select
                            value={row.rate_unit}
                            onValueChange={(value) =>
                              setRows((current) =>
                                current.map((currentRow) =>
                                  currentRow.rowId === row.rowId
                                    ? { ...currentRow, rate_unit: value as BillRateUnit }
                                    : currentRow,
                                ),
                              )
                            }
                          >
                            <SelectTrigger className="w-24">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="hourly">/hr</SelectItem>
                              <SelectItem value="daily">/day</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 pr-3">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.overtime_rate}
                            onChange={(e) =>
                              setRows((current) =>
                                current.map((currentRow) =>
                                  currentRow.rowId === row.rowId
                                    ? { ...currentRow, overtime_rate: e.target.value }
                                    : currentRow,
                                ),
                              )
                            }
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.doubletime_rate}
                            onChange={(e) =>
                              setRows((current) =>
                                current.map((currentRow) =>
                                  currentRow.rowId === row.rowId
                                    ? { ...currentRow, doubletime_rate: e.target.value }
                                    : currentRow,
                                ),
                              )
                            }
                          />
                        </td>
                        <td className="py-2 pr-0 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveRow(row.rowId)}
                            aria-label="Remove rate card row"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button type="button" onClick={handleSave} disabled={updateProject.isPending || hasIncompleteRow}>
            {updateProject.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ProjectZoneSpotSection({
  projectId,
  locationId,
  zone,
  spot,
}: {
  projectId: number;
  locationId: number;
  zone?: number | null;
  spot?: number | null;
}) {
  const updateProject = useUpdateProject();
  const { data: zonesData } = useZones({ location: locationId, page_size: 1000 });
  const { data: spotsData } = useSpots({ location: locationId, zone: zone ?? undefined, page_size: 200 });

  const zones = zonesData?.results ?? [];
  const spots = spotsData?.results ?? [];

  if (zones.length === 0 && spots.length === 0) return null;

  const handleZoneChange = (zoneId: number | null) => {
    updateProject.mutate({ id: projectId, data: { zone: zoneId, spot: null } });
  };

  const handleSpotChange = (val: string) => {
    updateProject.mutate({ id: projectId, data: { spot: val === 'none' ? null : parseInt(val) } });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Muster Point</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {zones.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Zone</Label>
            <ZonePicker
              locationId={locationId}
              value={zone ?? null}
              onChange={handleZoneChange}
              placeholder="No zone"
            />
          </div>
        )}
        {spots.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Spot</Label>
            <Select
              value={spot?.toString() ?? 'none'}
              onValueChange={handleSpotChange}
            >
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="No spot" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Spot</SelectItem>
                {spots.map((s) => (
                  <SelectItem key={s.id} value={s.id.toString()}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Tasks Tab ──────────────────────────────────────────────────────────────

function TasksTab({ projectId }: { projectId: number }) {
  const { data: tasks } = useProjectTasks({ project: projectId, page_size: 100 });
  const createTask = useCreateProjectTask();
  const updateTask = useUpdateProjectTask();
  const deleteTask = useDeleteProjectTask();

  const [showAddTask, setShowAddTask] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskName.trim()) return;
    await createTask.mutateAsync({
      project: projectId,
      title: newTaskName,
      status: 'todo',
      priority: 'medium',
      order: (tasks?.results.length ?? 0) + 1,
    });
    setNewTaskName('');
    setShowAddTask(false);
  };

  const handleToggleStatus = async (taskId: number, currentStatus: ProjectTaskStatus) => {
    const nextStatus: ProjectTaskStatus =
      currentStatus === 'completed' ? 'todo' : currentStatus === 'todo' ? 'in_progress' : 'completed';
    await updateTask.mutateAsync({ id: taskId, data: { status: nextStatus } });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Tasks ({tasks?.count ?? 0})</h3>
        <Button size="sm" onClick={() => setShowAddTask(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Add Task
        </Button>
      </div>

      {showAddTask && (
        <form onSubmit={handleAddTask} className="flex gap-2">
          <Input
            placeholder="Task name..."
            value={newTaskName}
            onChange={(e) => setNewTaskName(e.target.value)}
            autoFocus
          />
          <Button type="submit" disabled={createTask.isPending}>
            Add
          </Button>
          <Button type="button" variant="outline" onClick={() => setShowAddTask(false)}>
            Cancel
          </Button>
        </form>
      )}

      <div className="space-y-2">
        {tasks?.results.map((task) => (
          <Card key={task.id}>
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleToggleStatus(task.id, task.status)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    task.status === 'completed'
                      ? 'bg-green-500 border-green-500 text-white'
                      : 'border-gray-300 hover:border-blue-500'
                  }`}
                >
                  {task.status === 'completed' && <CheckCircle2 className="h-3 w-3" />}
                </button>
                <div>
                  <p
                    className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-400' : ''}`}
                  >
                    {task.title}
                  </p>
                  {task.assigned_to_name && (
                    <p className="text-xs text-gray-500">Assigned to: {task.assigned_to_name}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={taskStatusColors[task.status] ?? ''}>
                  {task.status.replace('_', ' ')}
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteTask.mutate(task.id)}
                >
                  <Trash2 className="h-4 w-4 text-gray-400" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {(!tasks || tasks.results.length === 0) && (
          <p className="text-gray-500 text-center py-8">No tasks yet</p>
        )}
      </div>
    </div>
  );
}

// ─── Team Tab ───────────────────────────────────────────────────────────────

function TeamTab({
  projectId,
  project,
  projectsEnabled,
}: {
  projectId: number;
  project: Project;
  projectsEnabled: boolean;
}) {
  const currency = useOrgCurrency();
  // Crew bill rates are H3Books money (server strips/rejects them while the
  // projects module is off). Team changes follow the server's job write rule.
  const { canManageJobs } = usePermissions();
  const canManageTeam = canManageJobs;
  const { data: assignments } = useProjectAssignments(projectId);
  const createAssignment = useCreateProjectAssignment();
  const deleteAssignment = useDeleteProjectAssignment();
  const [showAdd, setShowAdd] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<PersonOption | null>(null);
  const [role, setRole] = useState('technician');
  const [billRate, setBillRate] = useState('');
  const [billRateUnit, setBillRateUnit] = useState<BillRateUnit>('daily');
  const [addError, setAddError] = useState<string | null>(null);

  const defaultStartDate = project.start_date ?? getLocalToday();
  const [startDate, setStartDate] = useState(defaultStartDate);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPerson?.tech_id) return;
    setAddError(null);
    let assignment;
    try {
      assignment = await createAssignment.mutateAsync({
        project: projectId,
        tech: selectedPerson.tech_id,
        role,
        start_date: startDate,
        ...(projectsEnabled
          ? { daily_rate: billRate || undefined, rate_unit: billRateUnit }
          : {}),
      });
    } catch (err) {
      setAddError(handleApiError(err).message || 'Failed to add team member');
      return;
    }
    if (assignment.warnings?.length) {
      toast.warning('Assignment created with warnings', {
        description: assignment.warnings.map((warning) => warning.message).join(' '),
      });
    }
    setShowAdd(false);
    setSelectedPerson(null);
    setRole('technician');
    setBillRate('');
    setBillRateUnit('daily');
    setStartDate(defaultStartDate);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Team ({assignments?.length ?? 0})</h3>
        {canManageTeam && (
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Add Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Team Member</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4">
                {addError && (
                  <Alert variant="destructive">
                    <AlertDescription>{addError}</AlertDescription>
                  </Alert>
                )}
                <div
                  data-field-container
                  data-required="true"
                  data-required-empty={selectedPerson?.tech_id ? 'false' : 'true'}
                  data-required-message="Technician is required."
                  tabIndex={-1}
                >
                  <Label>Technician *</Label>
                  <PersonSearchComboBox
                    value={selectedPerson}
                    onChange={(person) => setSelectedPerson(person)}
                    placeholder="Search technicians by name..."
                    ineligibleMessage="External people cannot be assigned to a project team."
                  />
                </div>
                <div>
                  <Label>Role</Label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lead">Lead</SelectItem>
                      <SelectItem value="technician">Technician</SelectItem>
                      <SelectItem value="helper">Helper</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
                {projectsEnabled && (
                  <div>
                    <Label>Bill Rate</Label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={billRate}
                        onChange={(e) => setBillRate(e.target.value)}
                        placeholder="0.00"
                      />
                      <Select
                        value={billRateUnit}
                        onValueChange={(value) => setBillRateUnit(value as BillRateUnit)}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Per hour</SelectItem>
                          <SelectItem value="daily">Per day</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={createAssignment.isPending}>
                    Add
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="space-y-2">
        {assignments?.map((a) => (
          <Card key={a.id}>
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{a.tech_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{a.role}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {projectsEnabled && a.daily_rate && (
                  <span className="text-sm text-gray-500">
                    {formatMoney(a.daily_rate, currency, OPTIONAL_FRACTION_DIGITS)}
                    {billRateUnitSuffix(a.rate_unit)}
                  </span>
                )}
                {canManageTeam && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteAssignment.mutate(a.id)}
                  >
                    <Trash2 className="h-4 w-4 text-gray-400" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {(!assignments || assignments.length === 0) && (
          <p className="text-gray-500 text-center py-8">No team members assigned</p>
        )}
      </div>
    </div>
  );
}

// ─── Time Tab ───────────────────────────────────────────────────────────────

function describeTimeEntryError(err: unknown, fallback: string): string {
  const apiError = handleApiError(err);
  if (apiError.errors) {
    const fieldMessages = Object.entries(apiError.errors)
      .map(([field, messages]) =>
        field === 'non_field_errors' ? messages.join(' ') : `${field}: ${messages.join(' ')}`,
      )
      .join(' ');
    if (fieldMessages) return fieldMessages;
  }
  if (apiError.status === 403) {
    return apiError.message || "You don't have permission to log time for this worker.";
  }
  return apiError.message || fallback;
}

const OUTSIDE_ASSIGNMENT_WARNING =
  'This day is no longer in your assignment. Your manager will see this when approving.';

function describeOutsideAssignment(entry: TimeEntry, canLogForOthers: boolean): string {
  return canLogForOthers
    ? `${formatDateOnly(entry.date)} is outside ${entry.tech_name}'s assignment dates on this job.`
    : OUTSIDE_ASSIGNMENT_WARNING;
}

interface OwnEntryEdit {
  id: number;
  hours: string;
  description: string;
}

export function TimeTab({
  projectId,
  payrollEnabled,
  projectsEnabled,
}: {
  projectId: number;
  payrollEnabled: boolean;
  projectsEnabled: boolean;
}) {
  const timeEnabled = payrollEnabled || projectsEnabled;
  const currency = useOrgCurrency();
  const { isAdmin, isManager, hasMinModuleRole } = usePermissions();
  const { user } = useAuth();
  // Mirrors the backend rule: org Admins/Managers and H3Pay/H3Books managers
  // log, edit and approve for any assigned worker; everyone else logs own hours.
  const canLogForOthers =
    isAdmin ||
    isManager ||
    (payrollEnabled && hasMinModuleRole('payroll', 'manager')) ||
    (projectsEnabled && hasMinModuleRole('projects', 'manager'));
  const { data: entries, error: entriesError } = useProjectTimeEntries(projectId, { page_size: 100 }, timeEnabled);
  const { data: assignments } = useProjectAssignments(projectId);
  const approveEntry = useApproveTimeEntry();
  const selfApproval = useSelfApproval('time entry');
  const createEntry = useCreateProjectTimeEntry(projectId);
  const updateEntry = useUpdateTimeEntry();
  const [ownEdit, setOwnEdit] = useState<OwnEntryEdit | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [summaryDateFrom, setSummaryDateFrom] = useState('');
  const [summaryDateTo, setSummaryDateTo] = useState('');
  const { data: summaryData } = useProjectTimeSummary(projectId, {
    date_from: summaryDateFrom || undefined,
    date_to: summaryDateTo || undefined,
  }, timeEnabled);
  const [form, setForm] = useState({
    tech: '',
    date: getLocalToday(),
    hours: '',
    description: '',
    billable: true,
    cost_code: null as number | null,
  });

  if (!timeEnabled) {
    return <ModuleDisabledState moduleKey="payroll" />;
  }

  // Owner ruling (P6b): a tech may correct hours and notes on their own
  // unapproved entries. Billed entries are rejected by the API (409), and
  // managers keep their existing controls instead of this inline edit.
  const canEditOwnEntry = (entry: TimeEntry) =>
    !canLogForOthers &&
    !entry.approved &&
    !entry.is_locked &&
    user?.person_id != null &&
    entry.person === user.person_id;
  const showOwnEditActions = entries?.results.some(canEditOwnEntry) ?? false;

  const startOwnEdit = (entry: TimeEntry) => {
    setListError(null);
    setSaveWarning(null);
    setOwnEdit({ id: entry.id, hours: entry.hours, description: entry.description });
  };

  const handleOwnEditSave = async () => {
    if (!ownEdit || !ownEdit.hours) return;
    setListError(null);
    // The inline inputs sit outside a <form>, so mirror the Log Time form's
    // browser-enforced range here instead of relying on min/max attributes.
    const hoursValue = Number(ownEdit.hours);
    if (!Number.isFinite(hoursValue) || hoursValue < 0 || hoursValue > 24) {
      setListError('Hours must be between 0 and 24.');
      return;
    }
    let saved: TimeEntry;
    try {
      saved = await updateEntry.mutateAsync({
        id: ownEdit.id,
        data: { hours: ownEdit.hours, description: ownEdit.description },
      });
    } catch (err) {
      setListError(describeTimeEntryError(err, 'Failed to update time entry'));
      return;
    }
    setSaveWarning(
      saved.warning ?? (saved.outside_assignment ? OUTSIDE_ASSIGNMENT_WARNING : null),
    );
    setOwnEdit(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.hours) return;
    setFormError(null);
    const payload: Omit<CreateTimeEntryPayload, 'project'> = {
      date: form.date,
      hours: form.hours,
      description: form.description,
      billable: projectsEnabled ? form.billable : true,
      cost_code: form.cost_code,
    };
    if (canLogForOthers && form.tech) payload.tech = parseInt(form.tech);
    let saved: TimeEntry;
    try {
      saved = await createEntry.mutateAsync(payload);
    } catch (err) {
      setFormError(describeTimeEntryError(err, 'Failed to log time'));
      return;
    }
    setSaveWarning(saved.outside_assignment ? describeOutsideAssignment(saved, canLogForOthers) : null);
    setShowForm(false);
    setForm({
      tech: '',
      date: getLocalToday(),
      hours: '',
      description: '',
      billable: true,
      cost_code: null,
    });
  };

  const handleApprove = (entryId: number) => {
    setListError(null);
    void selfApproval.approve(entryId, (confirmSelfApproval) =>
      approveEntry.mutateAsync({ id: entryId, confirmSelfApproval }),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Time Entries ({entries?.count ?? 0})</h3>
        <Button size="sm" onClick={() => { setShowForm((v) => !v); setFormError(null); }}>
          <Plus className="h-4 w-4 mr-1" />
          Log Time
        </Button>
      </div>

      {entriesError && (
        <Alert variant="destructive">
          <AlertDescription>{describeTimeEntryError(entriesError, 'Failed to load time entries')}</AlertDescription>
        </Alert>
      )}
      {listError && (
        <Alert variant="destructive">
          <AlertDescription>{listError}</AlertDescription>
        </Alert>
      )}
      <ApprovalError message={selfApproval.errorMessage} />
      {selfApproval.dialog}
      {saveWarning && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{saveWarning}</AlertDescription>
        </Alert>
      )}

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {formError && (
                <Alert variant="destructive">
                  <AlertDescription>{formError}</AlertDescription>
                </Alert>
              )}
              <p className="text-sm text-gray-500">
                {canLogForOthers
                  ? 'As a manager you can log time for any tech assigned to this job. Leave the tech blank to log your own hours.'
                  : "You can log your own hours on jobs you're assigned to. Managers and payroll managers log time for other workers."}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {canLogForOthers ? (
                  <div>
                    <Label>Tech</Label>
                    <Select
                      value={form.tech}
                      onValueChange={(val) => setForm((p) => ({ ...p, tech: val }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Current user" />
                      </SelectTrigger>
                      <SelectContent>
                        {assignments?.map((a) => (
                          <SelectItem key={a.tech} value={a.tech.toString()}>
                            {a.tech_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <Label>Tech</Label>
                    <p className="text-sm py-2">Current user (your own hours)</p>
                  </div>
                )}
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label>Hours *</Label>
                  <Input
                    type="number"
                    step="0.25"
                    min="0"
                    max="24"
                    value={form.hours}
                    onChange={(e) => setForm((p) => ({ ...p, hours: e.target.value }))}
                    placeholder="0.0"
                    required
                  />
                </div>
                {payrollEnabled && (
                  <div>
                    <Label>Cost Code</Label>
                    <CostCodeSelector
                      projectId={projectId}
                      value={form.cost_code}
                      onChange={(id) => setForm((p) => ({ ...p, cost_code: id }))}
                      enabled={payrollEnabled}
                    />
                  </div>
                )}
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-4">
                {projectsEnabled && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="time-billable"
                      checked={form.billable}
                      onChange={(e) => setForm((p) => ({ ...p, billable: e.target.checked }))}
                    />
                    <Label htmlFor="time-billable">Billable</Label>
                  </div>
                )}
                <div className="flex-1" />
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createEntry.isPending}>
                  {createEntry.isPending ? 'Saving...' : 'Log Time'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {entries && entries.results.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-gray-500">
                    <th className="p-3 font-medium">Date</th>
                    <th className="p-3 font-medium">Tech</th>
                    <th className="p-3 font-medium">Hours</th>
                    <th className="p-3 font-medium">Cost Code</th>
                    <th className="p-3 font-medium">Description</th>
                    <th className="p-3 font-medium">Status</th>
                    {(canLogForOthers || showOwnEditActions) && (
                      <th className="p-3 font-medium">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {entries.results.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="p-3">{formatDateOnly(entry.date)}</td>
                      <td className="p-3">{entry.tech_name}</td>
                      <td className="p-3 font-medium">
                        {ownEdit?.id === entry.id ? (
                          <Input
                            type="number"
                            step="0.25"
                            min="0"
                            max="24"
                            aria-label="Hours"
                            className="w-24"
                            value={ownEdit.hours}
                            onChange={(e) =>
                              setOwnEdit((p) => (p ? { ...p, hours: e.target.value } : p))
                            }
                            required
                          />
                        ) : (
                          parseFloat(entry.hours).toFixed(1)
                        )}
                      </td>
                      <td className="p-3 text-gray-600">
                        {entry.cost_code_detail
                          ? `${entry.cost_code_detail.code} — ${entry.cost_code_detail.name}`
                          : '—'}
                      </td>
                      <td className="p-3 text-gray-600 max-w-xs truncate">
                        {ownEdit?.id === entry.id ? (
                          <Textarea
                            aria-label="Notes"
                            rows={2}
                            value={ownEdit.description}
                            onChange={(e) =>
                              setOwnEdit((p) => (p ? { ...p, description: e.target.value } : p))
                            }
                          />
                        ) : (
                          entry.description || '—'
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge variant={entry.approved ? 'default' : 'secondary'}>
                            {entry.approved ? 'Approved' : 'Pending'}
                          </Badge>
                          {entry.outside_assignment && (
                            <Badge
                              variant="outline"
                              className="border-amber-400 text-amber-700"
                              title="This day is no longer covered by the tech's assignment on this job."
                            >
                              Outside assignment
                            </Badge>
                          )}
                        </div>
                      </td>
                      {canLogForOthers && (
                        <td className="p-3">
                          {!entry.approved && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApprove(entry.id)}
                            >
                              Approve
                            </Button>
                          )}
                        </td>
                      )}
                      {!canLogForOthers && showOwnEditActions && (
                        <td className="p-3">
                          {canEditOwnEntry(entry) &&
                            (ownEdit?.id === entry.id ? (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={handleOwnEditSave}
                                  disabled={updateEntry.isPending || !ownEdit.hours}
                                >
                                  {updateEntry.isPending ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setOwnEdit(null)}
                                  disabled={updateEntry.isPending}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => startOwnEdit(entry)}>
                                Edit
                              </Button>
                            ))}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        !showForm && !entriesError && <p className="text-gray-500 text-center py-8">No time entries logged</p>
      )}

      {/* ─── Time Summary by Cost Code ─────────────────────────────────── */}
      <div className="mt-8 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Time Summary by Cost Code</h3>
          <a
            href={`/api/v1/projects/projects/${projectId}/time-summary/?export=csv${summaryDateFrom ? `&date_from=${summaryDateFrom}` : ''}${summaryDateTo ? `&date_to=${summaryDateTo}` : ''}`}
            download
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            <Download className="h-4 w-4" />
            Download CSV
          </a>
        </div>

        <div className="flex items-end gap-4">
          <div>
            <Label className="text-xs text-gray-500">From</Label>
            <Input
              type="date"
              value={summaryDateFrom}
              onChange={(e) => setSummaryDateFrom(e.target.value)}
              className="w-40"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">To</Label>
            <Input
              type="date"
              value={summaryDateTo}
              onChange={(e) => setSummaryDateTo(e.target.value)}
              className="w-40"
            />
          </div>
          {(summaryDateFrom || summaryDateTo) && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setSummaryDateFrom(''); setSummaryDateTo(''); }}
            >
              Clear
            </Button>
          )}
        </div>

        {summaryData && summaryData.length > 0 ? (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50 text-left text-gray-500">
                      <th className="p-3 font-medium">Cost Code</th>
                      <th className="p-3 font-medium">Name</th>
                      <th className="p-3 font-medium text-right">Total Hours</th>
                      {projectsEnabled && (<>
                        <th className="p-3 font-medium text-right">Billable Hours</th>
                        <th className="p-3 font-medium text-right">Total Cost</th>
                      </>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {summaryData.map((row) => (
                      <tr key={row.cost_code_id ?? 'uncategorized'} className="hover:bg-gray-50">
                        <td className="p-3 font-mono text-gray-600">{row.code ?? '—'}</td>
                        <td className="p-3">{row.name}</td>
                        <td className="p-3 text-right font-medium">
                          {parseFloat(row.total_hours).toFixed(2)}
                        </td>
                        {projectsEnabled && (<>
                          <td className="p-3 text-right">
                            {parseFloat(row.total_billable_hours).toFixed(2)}
                          </td>
                          <td className="p-3 text-right">
                            {row.total_cost == null ? '—' : formatMoney(row.total_cost, currency)}
                          </td>
                        </>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <p className="text-gray-500 text-center py-4">No summary data for selected period</p>
        )}
      </div>
    </div>
  );
}

// ─── Documents Tab ──────────────────────────────────────────────────────────

// ─── Notes Tab ──────────────────────────────────────────────────────────────

// ─── Financials Tab ─────────────────────────────────────────────────────────

function FinancialsTab({
  projectId,
  summary,
  project,
  showInvoicing,
}: {
  projectId: number;
  summary: ReturnType<typeof useProjectSummary>['data'];
  project: NonNullable<ReturnType<typeof useProject>['data']>;
  showInvoicing: boolean;
}) {
  const currency = useOrgCurrency();
  const { data: invoices } = useInvoices({ project: projectId, page_size: 50 });

  // Budget alert calculations
  const budget = summary ? parseFloat(summary.budget ?? '0') : 0;
  const totalCosts = summary ? parseFloat(summary.total_costs || '0') : 0;
  const budgetUsedPercent = budget > 0 ? (totalCosts / budget) * 100 : 0;
  const threshold = parseFloat(project.budget_alert_threshold ?? '0') * 100;
  const isOverThreshold = budgetUsedPercent >= threshold;
  const isOverBudget = budgetUsedPercent > 100;

  const getProgressColor = () => {
    if (budgetUsedPercent > 90) return 'bg-red-500';
    if (budgetUsedPercent > 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="space-y-6">
      {/* Budget Alert Banner */}
      {isOverThreshold && budget > 0 && (
        <div className={`flex items-center gap-3 p-4 rounded-lg border ${isOverBudget ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <AlertTriangle className={`h-5 w-5 ${isOverBudget ? 'text-red-600' : 'text-yellow-600'}`} />
          <div>
            <p className={`font-medium ${isOverBudget ? 'text-red-800' : 'text-yellow-800'}`}>
              {isOverBudget ? 'Over Budget!' : 'Budget Alert'}
            </p>
            <p className={`text-sm ${isOverBudget ? 'text-red-600' : 'text-yellow-600'}`}>
              Project has used {budgetUsedPercent.toFixed(1)}% of its budget (threshold: {threshold}%)
            </p>
          </div>
        </div>
      )}

      {/* Budget Progress Bar */}
      {budget > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span className="text-gray-600">Budget Usage</span>
              <span className="font-medium">{budgetUsedPercent.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${getProgressColor()}`}
                style={{ width: `${Math.min(budgetUsedPercent, 100)}%` }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-gray-500">
              <span>{formatMoney(totalCosts, currency, OPTIONAL_FRACTION_DIGITS)} spent</span>
              <span>{formatMoney(budget, currency, OPTIONAL_FRACTION_DIGITS)} budget</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Budget vs Actual */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-500">Budget</p>
            <p className="text-2xl font-bold text-blue-600">
              {summary?.budget ? formatMoney(summary.budget, currency, OPTIONAL_FRACTION_DIGITS) : '—'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-500">Actual Costs</p>
            <p className="text-2xl font-bold text-orange-600">
              {summary ? formatMoney(summary.total_costs || '0', currency, OPTIONAL_FRACTION_DIGITS) : '—'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-500">
              {summary && parseFloat(summary.budget ?? '0') - parseFloat(summary.total_costs || '0') >= 0 ? 'Under Budget' : 'Over Budget'}
            </p>
            <p
              className={`text-2xl font-bold ${
                summary && parseFloat(summary.budget ?? '0') - parseFloat(summary.total_costs || '0') >= 0
                  ? 'text-green-600'
                  : 'text-red-600'
              }`}
            >
              {summary
                ? formatMoney(Math.abs(parseFloat(summary.budget ?? '0') - parseFloat(summary.total_costs || '0')), currency, OPTIONAL_FRACTION_DIGITS)
                : '—'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* P&L Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">P&amp;L Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            {showInvoicing && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Total Invoiced</span>
                <span className="font-medium">
                  {summary ? formatMoney(summary.total_invoiced || '0', currency, OPTIONAL_FRACTION_DIGITS) : '—'}
                </span>
              </div>
            )}
            {showInvoicing && (
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Total Payments Received</span>
                <span className="font-medium">
                  {summary ? formatMoney(summary.total_paid || '0', currency, OPTIONAL_FRACTION_DIGITS) : '—'}
                </span>
              </div>
            )}
            <div className="flex justify-between py-2 border-b">
              <span className="text-gray-600">Total Costs</span>
              <span className="font-medium">
                {summary ? formatMoney(summary.total_costs || '0', currency, OPTIONAL_FRACTION_DIGITS) : '—'}
              </span>
            </div>
            {showInvoicing && (
              <div className="flex justify-between py-2 font-semibold">
                <span>Profit / Loss</span>
                <span className={summary && parseFloat(summary.total_paid || '0') - parseFloat(summary.total_costs || '0') >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {summary
                    ? formatMoney(parseFloat(summary.total_paid || '0') - parseFloat(summary.total_costs || '0'), currency, OPTIONAL_FRACTION_DIGITS)
                    : '—'}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Retainage Summary */}
      {showInvoicing && parseFloat(project.retainage_percent) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Retainage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between py-2 border-b">
                <span className="text-gray-600">Contract Retainage Rate</span>
                <span className="font-medium">{project.retainage_percent}%</span>
              </div>
              <div className="flex justify-between py-2 font-semibold">
                <span>Retainage Withheld to Date</span>
                <span className="text-orange-600">
                  {formatMoney(project.retainage_withheld_to_date || '0', currency, OPTIONAL_FRACTION_DIGITS)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices */}
      {showInvoicing && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Invoices ({invoices?.count ?? 0})</CardTitle>
          </CardHeader>
          <CardContent>
            {invoices && invoices.results.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 font-medium">Invoice #</th>
                      <th className="pb-2 font-medium">Amount</th>
                      <th className="pb-2 font-medium">Paid</th>
                      <th className="pb-2 font-medium">Balance</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium">Due Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {invoices.results.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="py-2 font-medium">{inv.invoice_number}</td>
                        <td className="py-2">{formatMoney(inv.total, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                        <td className="py-2">{formatMoney(inv.amount_paid, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                        <td className="py-2 font-medium">{formatMoney(inv.balance_due, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                        <td className="py-2">
                          <Badge variant="secondary">{inv.status}</Badge>
                        </td>
                        <td className="py-2 text-gray-600">
                          {formatDateOnly(inv.due_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No invoices</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Change Orders Tab ─────────────────────────────────────────────────────

// ─── Progress / SOV Tab ─────────────────────────────────────────────────────

function ProgressTab({ projectId, project }: { projectId: number; project: Project }) {
  const currency = useOrgCurrency();
  const { data: progressSummary, isLoading } = useProgressSummary(projectId);
  const generateInvoice = useGenerateProgressInvoice(projectId);
  const updateProject = useUpdateProject();

  const [contractValue, setContractValue] = useState(project.contract_value ?? '');
  const [retainagePercent, setRetainagePercent] = useState(project.retainage_percent ?? '');

  useEffect(() => {
    setContractValue(project.contract_value ?? '');
    setRetainagePercent(project.retainage_percent ?? '');
  }, [project.contract_value, project.retainage_percent]);

  const handleSaveBilling = async () => {
    try {
      await updateProject.mutateAsync({
        id: projectId,
        data: {
          contract_value: contractValue.trim() === '' ? undefined : contractValue.trim(),
          retainage_percent: retainagePercent.trim() === '' ? undefined : retainagePercent.trim(),
        },
      });
      toast.success('Billing configuration saved');
    } catch (_err) {
      toast.error('Failed to save billing configuration');
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      await generateInvoice.mutateAsync();
      toast.success('Progress invoice generated');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert(`Failed to generate progress invoice: ${message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!progressSummary) {
    return <p className="text-gray-500 text-sm py-4">No progress data available.</p>;
  }

  const { rows, totals, retainage_rate, current_contract_value } = progressSummary;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Progress Billing Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="contract_value">Contract Value ({currency})</Label>
              <Input
                id="contract_value"
                type="number"
                step="0.01"
                value={contractValue}
                onChange={(e) => setContractValue(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="retainage_percent">Retainage (%)</Label>
              <Input
                id="retainage_percent"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={retainagePercent}
                onChange={(e) => setRetainagePercent(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                onClick={handleSaveBilling}
                disabled={updateProject.isPending}
              >
                {updateProject.isPending ? 'Saving…' : 'Save Billing'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Schedule of Values — Progress Billing</h3>
          <p className="text-sm text-gray-500">
            Contract value: {formatMoney(current_contract_value, currency, OPTIONAL_FRACTION_DIGITS)}
            {parseFloat(retainage_rate) > 0 && ` · Retainage: ${retainage_rate}%`}
          </p>
        </div>
        <Button
          onClick={handleGenerateInvoice}
          disabled={generateInvoice.isPending || parseFloat(totals.this_period) <= 0}
        >
          <DollarSign className="h-4 w-4 mr-1" />
          {generateInvoice.isPending ? 'Generating…' : 'Generate Progress Invoice'}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium">Description</th>
                  <th className="text-right p-3 font-medium">Budget</th>
                  <th className="text-right p-3 font-medium">% Complete</th>
                  <th className="text-right p-3 font-medium">Earned to Date</th>
                  <th className="text-right p-3 font-medium">Previously Billed</th>
                  <th className="text-right p-3 font-medium">This Period</th>
                  {parseFloat(retainage_rate) > 0 && (
                    <th className="text-right p-3 font-medium">Retainage</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.phase_id} className="border-b hover:bg-gray-50">
                    <td className="p-3">{row.description}</td>
                    <td className="p-3 text-right">{formatMoney(row.budget, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                    <td className="p-3 text-right">{row.percent_complete}%</td>
                    <td className="p-3 text-right">{formatMoney(row.earned_to_date, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                    <td className="p-3 text-right">{formatMoney(row.previously_billed, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                    <td className="p-3 text-right font-medium">
                      {parseFloat(row.this_period) > 0 ? formatMoney(row.this_period, currency, OPTIONAL_FRACTION_DIGITS) : '—'}
                    </td>
                    {parseFloat(retainage_rate) > 0 && (
                      <td className="p-3 text-right">{formatMoney(row.retainage, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-100 font-semibold">
                  <td className="p-3">Totals</td>
                  <td className="p-3 text-right">{formatMoney(totals.budget, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                  <td className="p-3 text-right">—</td>
                  <td className="p-3 text-right">{formatMoney(totals.earned_to_date, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                  <td className="p-3 text-right">{formatMoney(totals.previously_billed, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                  <td className="p-3 text-right">{formatMoney(totals.this_period, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                  {parseFloat(retainage_rate) > 0 && (
                    <td className="p-3 text-right">{formatMoney(totals.retainage, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                  )}
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {rows.length === 0 && (
        <p className="text-gray-500 text-sm text-center py-4">
          No phases with budget amounts configured. Add budget amounts to project phases to use progress billing.
        </p>
      )}

      <PhaseManager projectId={projectId} />
    </div>
  );
}

function PhaseManager({ projectId }: { projectId: number }) {
  const currency = useOrgCurrency();
  const { data: phases, isLoading } = useProjectPhases(projectId);
  const createPhase = useCreateProjectPhase();
  const updatePhase = useUpdateProjectPhase();
  const deletePhase = useDeleteProjectPhase();

  const [newPhase, setNewPhase] = useState({ name: '', budget: '', percent: '' });
  const [edits, setEdits] = useState<Record<number, { name: string; budget: string; percent: string }>>({});

  useEffect(() => {
    if (phases) {
      setEdits(
        Object.fromEntries(
          phases.map((p) => [
            p.id,
            { name: p.name, budget: p.budget_amount ?? '', percent: p.percent_complete ?? '' },
          ])
        )
      );
    }
  }, [phases]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPhase.name.trim()) return;
    await createPhase.mutateAsync({
      project: projectId,
      name: newPhase.name.trim(),
      budget_amount: newPhase.budget || undefined,
      percent_complete: newPhase.percent || '0',
      order: (phases?.length ?? 0),
      status: 'pending',
    });
    setNewPhase({ name: '', budget: '', percent: '' });
  };

  const handleUpdate = async (phase: ProjectPhase) => {
    const edit = edits[phase.id];
    if (!edit) return;
    await updatePhase.mutateAsync({
      id: phase.id,
      data: {
        name: edit.name,
        budget_amount: edit.budget || undefined,
        percent_complete: edit.percent || '0',
      },
    });
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this phase?')) return;
    await deletePhase.mutateAsync(id);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Phase Management</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left">
                  <th className="p-3 font-medium">Phase</th>
                  <th className="p-3 font-medium w-40">Budget ({currency})</th>
                  <th className="p-3 font-medium w-32">% Complete</th>
                  <th className="p-3 font-medium w-24"></th>
                </tr>
              </thead>
              <tbody>
                {phases?.map((phase) => {
                  const edit = edits[phase.id] ?? { name: phase.name, budget: phase.budget_amount ?? '', percent: phase.percent_complete ?? '' };
                  return (
                    <tr key={phase.id} className="border-b">
                      <td className="p-2">
                        <Input
                          value={edit.name}
                          onChange={(e) =>
                            setEdits((prev) => ({ ...prev, [phase.id]: { ...edit, name: e.target.value } }))
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={edit.budget}
                          onChange={(e) =>
                            setEdits((prev) => ({ ...prev, [phase.id]: { ...edit, budget: e.target.value } }))
                          }
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={edit.percent}
                          onChange={(e) =>
                            setEdits((prev) => ({ ...prev, [phase.id]: { ...edit, percent: e.target.value } }))
                          }
                        />
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdate(phase)}
                            disabled={updatePhase.isPending}
                          >
                            Save
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(phase.id)}
                            disabled={deletePhase.isPending}
                          >
                            <Trash2 className="h-4 w-4 text-gray-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-b bg-gray-50">
                  <td className="p-2">
                    <Input
                      placeholder="New phase name"
                      value={newPhase.name}
                      onChange={(e) => setNewPhase((p) => ({ ...p, name: e.target.value }))}
                      required
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={newPhase.budget}
                      onChange={(e) => setNewPhase((p) => ({ ...p, budget: e.target.value }))}
                    />
                  </td>
                  <td className="p-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="0"
                      value={newPhase.percent}
                      onChange={(e) => setNewPhase((p) => ({ ...p, percent: e.target.value }))}
                    />
                  </td>
                  <td className="p-2">
                    <Button
                      type="button"
                      size="sm"
                      data-validate-required="true"
                      onClick={handleCreate}
                      disabled={createPhase.isPending}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const changeOrderStatusColors: Record<ChangeOrderStatus, string> = {
  proposed: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

function ChangeOrdersTab({ projectId }: { projectId: number }) {
  const currency = useOrgCurrency();
  const timeZone = useOrgTimeZone();
  const { data: changeOrders } = useChangeOrders({ project: projectId });
  const { data: project } = useProject(projectId);
  const createChangeOrder = useCreateChangeOrder();
  const approveChangeOrder = useApproveChangeOrder();
  const rejectChangeOrder = useRejectChangeOrder();
  const createLineItem = useCreateChangeOrderLineItem();
  const deleteLineItem = useDeleteChangeOrderLineItem();
  const [showAdd, setShowAdd] = useState(false);
  const [lineItemCOId, setLineItemCOId] = useState<number | null>(null);
  const [lineForm, setLineForm] = useState({ description: '', quantity: '', unit_price: '' });
  const [form, setForm] = useState({
    title: '',
    description: '',
    reason: '',
    amount: '',
    schedule_impact_days: '',
    client_approval_required: false,
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    await createChangeOrder.mutateAsync({
      project: projectId,
      title: form.title,
      description: form.description,
      reason: form.reason,
      amount: form.amount,
      schedule_impact_days: form.schedule_impact_days ? parseInt(form.schedule_impact_days) : null,
      client_approval_required: form.client_approval_required,
    });
    setForm({ title: '', description: '', reason: '', amount: '', schedule_impact_days: '', client_approval_required: false });
    setShowAdd(false);
  };

  const handleAddLineItem = async (coId: number) => {
    await createLineItem.mutateAsync({
      change_order: coId,
      description: lineForm.description,
      quantity: lineForm.quantity,
      unit_price: lineForm.unit_price,
    });
    setLineForm({ description: '', quantity: '', unit_price: '' });
    setLineItemCOId(null);
  };

  return (
    <div className="space-y-4">
      {/* Contract Value Summary */}
      {project?.contract_value && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Original Contract Value</p>
                <p className="text-lg font-semibold">
                  {project.original_contract_value ? formatMoney(project.original_contract_value, currency, OPTIONAL_FRACTION_DIGITS) : '—'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Current Contract Value</p>
                <p className="text-lg font-bold text-blue-600">
                  {formatMoney(project.current_contract_value ?? project.contract_value ?? '0', currency, OPTIONAL_FRACTION_DIGITS)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Change Orders ({changeOrders?.length ?? 0})</h3>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              New Change Order
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Change Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div>
                <Label>Reason</Label>
                <Input
                  value={form.reason}
                  onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount (+ increase, - decrease) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label>Schedule Impact (days)</Label>
                  <Input
                    type="number"
                    step="1"
                    placeholder="0"
                    value={form.schedule_impact_days}
                    onChange={(e) => setForm((p) => ({ ...p, schedule_impact_days: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="client_approval"
                  checked={form.client_approval_required}
                  onChange={(e) => setForm((p) => ({ ...p, client_approval_required: e.target.checked }))}
                />
                <Label htmlFor="client_approval">Requires client approval</Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createChangeOrder.isPending}>
                  {createChangeOrder.isPending ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {changeOrders?.map((co) => (
          <Card key={co.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-gray-400">{co.co_number}</span>
                    <p className="font-medium">{co.title}</p>
                    <Badge className={changeOrderStatusColors[co.status as ChangeOrderStatus] ?? ''}>
                      {co.status}
                    </Badge>
                    {co.client_approval_required && (
                      <Badge variant="outline">Client Approval Required</Badge>
                    )}
                  </div>
                  {co.description && (
                    <p className="text-sm text-gray-600">{co.description}</p>
                  )}
                  {co.reason && (
                    <p className="text-sm text-gray-500">Reason: {co.reason}</p>
                  )}
                  {co.schedule_impact_days != null && co.schedule_impact_days !== 0 && (
                    <p className="text-sm text-orange-600">
                      Schedule impact: {co.schedule_impact_days > 0 ? '+' : ''}{co.schedule_impact_days} day{Math.abs(co.schedule_impact_days) !== 1 ? 's' : ''}
                    </p>
                  )}
                  <p className="text-xs text-gray-400">
                    Proposed by {co.proposed_by_name} on{' '}
                    {formatDateTimeInTimeZone(co.created_at, timeZone, { dateStyle: 'medium' })}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-bold ${parseFloat(co.amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {parseFloat(co.amount) >= 0 ? '+' : ''}{formatMoney(co.amount, currency, OPTIONAL_FRACTION_DIGITS)}
                  </p>
                  {co.status === 'proposed' && (
                    <div className="flex gap-1 mt-2">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => approveChangeOrder.mutate(co.id)}
                        disabled={approveChangeOrder.isPending}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => rejectChangeOrder.mutate(co.id)}
                        disabled={rejectChangeOrder.isPending}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Line Items */}
              {co.line_items && co.line_items.length > 0 && (
                <div className="mt-3 border-t pt-3">
                  <p className="text-xs font-semibold text-gray-500 mb-1">Line Items</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 text-xs">
                        <th className="pb-1">Description</th>
                        <th className="pb-1 text-right">Qty</th>
                        <th className="pb-1 text-right">Unit Price</th>
                        <th className="pb-1 text-right">Total</th>
                        <th className="pb-1 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {co.line_items.map((item) => (
                        <tr key={item.id} className="border-t border-gray-100">
                          <td className="py-1">{item.description}</td>
                          <td className="py-1 text-right">{item.quantity}</td>
                          <td className="py-1 text-right">{formatMoney(item.unit_price, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                          <td className="py-1 text-right font-medium">{formatMoney(item.total, currency, OPTIONAL_FRACTION_DIGITS)}</td>
                          <td className="py-1 text-right">
                            {co.status === 'proposed' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                                onClick={() => deleteLineItem.mutate(item.id)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Add Line Item */}
              {co.status === 'proposed' && (
                <div className="mt-2">
                  {lineItemCOId === co.id ? (
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Input
                          placeholder="Description"
                          value={lineForm.description}
                          onChange={(e) => setLineForm((p) => ({ ...p, description: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="w-20">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Qty"
                          value={lineForm.quantity}
                          onChange={(e) => setLineForm((p) => ({ ...p, quantity: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="w-24">
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={`Unit ${currency}`}
                          value={lineForm.unit_price}
                          onChange={(e) => setLineForm((p) => ({ ...p, unit_price: e.target.value }))}
                          required
                        />
                      </div>
                      <Button
                        size="sm"
                        data-validate-required="true"
                        onClick={() => handleAddLineItem(co.id)}
                        disabled={createLineItem.isPending}
                      >
                        Add
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setLineItemCOId(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-xs"
                      onClick={() => { setLineItemCOId(co.id); setLineForm({ description: '', quantity: '', unit_price: '' }); }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Line Item
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {(!changeOrders || changeOrders.length === 0) && (
          <p className="text-gray-500 text-center py-8">No change orders</p>
        )}
      </div>
    </div>
  );
}

// ─── Gantt Chart Tab ───────────────────────────────────────────────────────
